import { Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { SuccessPortfolioCovenantGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery } from '../requests/portfolioCovenants.gql.generated';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import {
  errorToastConfig,
  getDefaultDashboardDisplayEndDate,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CovenantChartTemplate } from 'packages/portfolio/ui/covenants/CovenantChartTemplate';
import { createCovenantsChartConfig } from 'packages/portfolio/ui/covenants/CovenantsContainer';
import {
  covenantsInitialStartDate,
  defaultPortfolioCovenantValueType,
  getAllUniqueCovenantKeys,
} from 'packages/portfolio/ui/shared/portfolioChartUtils';

export const PortfolioCovenantsContainer = () => {
  const toast = useToast();
  const { creditFacilityId } = useCreditFacilityContext();

  const [
    allPortfolioCovenantsInDefaultRange,
    setAllPortfolioCovenantsInDefaultRange,
  ] = useState<SuccessPortfolioCovenantGQLType[]>([]);

  const {
    loading: isPortfolioCovenantsLoading,
    data: allPortfolioCovenantsData,
    refetch: refetchSuccessPortfolioCovenants,
  } = useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      if (data.getSuccessPortfolioCovenantsByCreditFacilityId.length) {
        // set the initial set so all configs within the longer initial range are considered
        if (!allPortfolioCovenantsInDefaultRange.length) {
          setAllPortfolioCovenantsInDefaultRange(
            data.getSuccessPortfolioCovenantsByCreditFacilityId,
          );
        }
      }
    },
    variables: {
      creditFacilityId,
      mode: 'RESULT',
      startDate: covenantsInitialStartDate,
      endDate: getDefaultDashboardDisplayEndDate(),
    },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
  });

  if (
    isPortfolioCovenantsLoading ||
    !allPortfolioCovenantsData ||
    !creditAgreementData ||
    isCreditAgreementLoading
  ) {
    return (
      <Skeleton height="600px" data-testid="credit-agreements-route-loading" />
    );
  }

  const creditAgreementString =
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement;
  const creditAgreementId =
    creditAgreementData?.currentCreditAgreementFullArtifact?.id;

  let parsedCreditAgreement = null;
  if (creditAgreementString) {
    try {
      parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );
    } catch (err) {
      Sentry.captureException(err);

      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
      });
    }
  }
  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );

  const { portfolioCovenants } = parsedCreditAgreement;

  if (
    !allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId
      .length
  ) {
    return (
      <InfoAlertWithDescription description="No covenant calculations are available in the selected time period." />
    );
  }

  const uniquePortfolioCovenantKeys = getAllUniqueCovenantKeys(
    allPortfolioCovenantsInDefaultRange,
  );

  return (
    <>
      {_.map(uniquePortfolioCovenantKeys, (portfolioCovenantKey) => {
        // it's possible for the covenantKey of a portfolio covenant calculation to no longer exist in the current CA, if it's been deleted
        // we will still show the calculations and graphs as historical data, but will not be able to parse out the thresholdType (min vs max) later downstream, which is fine
        const creditAgreementPortfolioCovenant = _.find(
          portfolioCovenants,
          (portfolioCovenant) =>
            portfolioCovenant.covenantKey === portfolioCovenantKey,
        );

        const valueType =
          creditAgreementPortfolioCovenant?._config_?.valueType ??
          defaultPortfolioCovenantValueType;

        return (
          <CovenantChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle={commonUtils.toTitleCase(portfolioCovenantKey)}
            covenantKey={portfolioCovenantKey}
            chartConfig={createCovenantsChartConfig(valueType)}
            key={`portfolio-covenant-chart-${portfolioCovenantKey}`}
            creditAgreementCovenant={creditAgreementPortfolioCovenant}
            covenantData={
              allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId
            }
            refetchCovenants={refetchSuccessPortfolioCovenants}
            covenantType="portfolio"
            valueType={valueType}
          />
        );
      })}
    </>
  );
};
